import { useState, type FormEvent } from 'react'
import { normaliseServerUrl, setServer, storedServer } from '../api/client'
import { probeServer } from '../api/endpoints'
import type { ServerInfo } from '../api/types'
import { Centered } from '../components/Centered'

/**
 * Which server this dashboard is for.
 *
 * Checked before it is saved, for the same reason the app checks it: accepting any string and
 * letting every later request fail somewhere else is how somebody concludes the software is broken
 * when the address was simply wrong.
 */
export function ConnectScreen({
  initial,
  onConnected,
}: {
  initial: string
  onConnected: (url: string, info: ServerInfo) => void
}) {
  const [url, setUrl] = useState(initial || storedServer())
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    const candidate = normaliseServerUrl(url)
    if (!candidate) {
      setError('Enter the address of your server.')
      return
    }

    setBusy(true)
    setError(null)

    // Saved before probing so the client has somewhere to send the request, and rolled back if it
    // turns out not to be a TacMaps server.
    const previous = storedServer()
    setServer(candidate)

    const result = await probeServer()
    setBusy(false)

    if (!result.ok) {
      setServer(previous)
      setError(result.error.message)
      return
    }
    if (result.value.product !== 'tacmaps') {
      setServer(previous)
      setError(`Something answered at that address, but it is not a TacMaps server.`)
      return
    }

    onConnected(candidate, result.value)
  }

  return (
    <Centered>
      <form className="card stack" onSubmit={submit} style={{ width: 380 }}>
        <div>
          <h1>TacMaps TOC</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            The operations dashboard for whoever is running the event.
          </p>
        </div>

        <div className="field">
          <label htmlFor="server">Server address</label>
          <input
            id="server"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="tacmaps.myclub.it"
            autoFocus
          />
          <p className="tertiary" style={{ marginTop: 6 }}>
            An address on your network works too, such as 192.168.1.50:8080.
          </p>
        </div>

        {error && <div className="banner danger">{error}</div>}

        <button type="submit" disabled={busy}>
          {busy ? 'Checking…' : 'Connect'}
        </button>
      </form>
    </Centered>
  )
}
